import { z } from "zod";
import { OptionKey } from "./model.ts";

/**
 * Gói đề: thứ DUY NHẤT trình duyệt nhận về một đề trước khi câu hỏi được chấm.
 * Mọi schema ở đây là `strictObject`: thêm một trường ngoài danh sách là lỗi ngay khi dựng gói,
 * nên không thể vô tình để lọt đáp án hay lời giải.
 */

export const BundleQuestion = z.strictObject({
  id: z.uuid(),
  number: z.number().int().positive(),
  part: z.number().int().min(1).max(7),
  passageId: z.uuid().nullable(),
  /** HTML đã sanitize. Rỗng ở Part 1–2 và ở câu điền từ của Part 6. */
  prompt: z.string(),
  options: z
    .array(z.strictObject({ key: OptionKey, text: z.string() }))
    .min(3)
    .max(4),
});

export type BundleQuestion = z.infer<typeof BundleQuestion>;

export const BundlePassage = z.strictObject({
  id: z.uuid(),
  part: z.number().int().min(1).max(7),
  sort: z.number().int().nonnegative(),
  /** Bài đọc không có đánh dấu dẫn chứng. Null với cụm bài nghe. */
  html: z.string().nullable(),
  audioKey: z.string().nullable(),
  imageKeys: z.array(z.string()),
  docCount: z.number().int().min(1).max(3),
});

export const TestBundle = z.strictObject({
  testId: z.uuid(),
  code: z.string(),
  name: z.string(),
  version: z.number().int().positive(),
  parts: z.array(z.number().int().min(1).max(7)),
  durationMin: z.number().int().positive(),
  passages: z.array(BundlePassage),
  questions: z.array(BundleQuestion),
});
export type TestBundle = z.infer<typeof TestBundle>;

/** Dựng và kiểm tra gói đề. Ném lỗi nếu đầu vào mang bất kỳ trường nào ngoài schema. */
export function buildTestBundle(input: z.input<typeof TestBundle>): TestBundle {
  return TestBundle.parse(input);
}

// Snapshot có cả dữ liệu riêng tư. Các object này chỉ lấy trường công khai;
// không spread snapshot vào gói đề, kể cả options có thêm trường trong tương lai.
const SnapshotQuestion = z.object({
  ...BundleQuestion.shape,
  options: z.array(z.object(BundleQuestion.shape.options.element.shape)).min(3).max(4),
});
const SnapshotPassage = z.object({
  ...BundlePassage.omit({ html: true }).shape,
  publicHtml: z.string().nullable(),
});
const BundleSnapshot = z.object({
  test: z.object(
    TestBundle.omit({ testId: true, version: true, passages: true, questions: true }).shape,
  ),
  passages: z.array(SnapshotPassage),
  questions: z.array(SnapshotQuestion),
});

/** Dựng gói công khai từ snapshot bất biến, bỏ toàn bộ trường riêng tư. */
export function buildTestBundleFromSnapshot(
  testId: string,
  version: number,
  snapshot: unknown,
): TestBundle {
  const saved = BundleSnapshot.parse(snapshot);
  return buildTestBundle({
    ...saved.test,
    testId,
    version,
    passages: saved.passages
      .map(({ publicHtml, ...passage }) => ({ ...passage, html: publicHtml }))
      .sort((a, b) => a.sort - b.sort),
    questions: saved.questions.sort((a, b) => a.number - b.number),
  });
}

/** Tên khoá không bao giờ được xuất hiện ở bất kỳ tầng nào của gói đề. */
const FORBIDDEN_KEYS = new Set([
  "correct",
  "explanation",
  "translation",
  "transcript",
  "translationVi",
  "skillTagId",
  "skill",
  "trapKey",
  "trap",
  "difficulty",
  "level",
  "poolKey",
  "isCrosstext",
  "isFree",
  "contentHash",
  "vocab",
  "why",
  "tip",
  "optV",
  "optAn",
]);

/** Dấu vết của đánh dấu dẫn chứng trong HTML. */
const EVIDENCE_MARKERS = [/data-ev\b/, /class="[^"]*\bev2?\b[^"]*"/];

/**
 * Soát một gói đề (hoặc bất kỳ giá trị JSON nào sắp gửi cho người học trước khi chấm).
 * Trả về danh sách chỗ rò rỉ; rỗng nghĩa là sạch. Đây là lớp kiểm tra thứ hai, độc lập với schema.
 */
export function findLeaks(value: unknown, path = "$"): string[] {
  if (typeof value === "string") {
    return EVIDENCE_MARKERS.some((re) => re.test(value)) ? [`${path}: còn đánh dấu dẫn chứng`] : [];
  }
  if (Array.isArray(value)) return value.flatMap((v, i) => findLeaks(v, `${path}[${i}]`));
  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, v]) => [
      ...(FORBIDDEN_KEYS.has(key) ? [`${path}.${key}: khoá bị cấm`] : []),
      ...findLeaks(v, `${path}.${key}`),
    ]);
  }
  return [];
}
